import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FileSpreadsheet, Copy, Plus, Trash2, Loader2, KeyRound, ExternalLink, ShieldCheck, ShieldOff, ClipboardCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * SheetsSyncPanel — Meta Lead Ads (Google Sheets) → LeadForge
 *  * Generates a scoped (`leads:create`) API key
 *  * Hands the admin a copy-paste Apps Script + step-by-step install
 *  * Column-mapping editor (auto-detect + manual override)
 */
export default function SheetsSyncPanel() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null); // shows raw once
  const [scriptModal, setScriptModal] = useState(null);          // {script, install_steps, business_name}
  const [mappings, setMappings] = useState([]);
  const [canonicalFields, setCanonicalFields] = useState([]);
  const [mappingEditor, setMappingEditor] = useState(null);      // active editor state
  const [businessName, setBusinessName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: keyRes }, { data: mapRes }, meRes] = await Promise.all([
        api.get("/api-keys"),
        api.get("/integrations/sheets-sync/mappings"),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      const syncKeys = (keyRes.api_keys || []).filter((k) => (k.scopes || []).includes("leads:create"));
      setKeys(syncKeys);
      setMappings(mapRes.mappings || []);
      setCanonicalFields(mapRes.canonical_fields || []);
      const bn = meRes?.data?.business?.business_name || meRes?.data?.user?.business_name || meRes?.data?.business_name || "";
      setBusinessName(bn);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createKey = async () => {
    setCreating(true);
    try {
      const nickname = window.prompt("Name this key (e.g. \"Meta Lead Ads · Rock River\")", "Meta Lead Ads · Sheets Sync");
      if (!nickname) { setCreating(false); return; }
      const { data } = await api.post("/api-keys", { name: nickname, scopes: ["leads:create"] });
      setNewlyCreatedKey(data);
      toast.success("API key created — copy it now, it won't be shown again.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const revokeKey = async (id) => {
    if (!window.confirm("Revoke this key? Anything using it will stop syncing.")) return;
    try { await api.post(`/api-keys/${id}/revoke`); toast.success("Key revoked"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const showScript = async (keyId) => {
    try {
      const { data } = await api.get(`/integrations/sheets-sync/apps-script?api_key_id=${keyId}`);
      setScriptModal(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copyToClipboard = async (text, label = "Copied") => {
    try { await navigator.clipboard.writeText(text); toast.success(label); }
    catch { toast.error("Clipboard blocked — select manually"); }
  };

  const deleteMapping = async (id) => {
    if (!window.confirm("Delete this mapping? The Apps Script will fall back to auto-detect.")) return;
    try { await api.delete(`/integrations/sheets-sync/mappings/${id}`); toast.success("Mapping deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="sheets-sync-panel">
      <div className="flex items-center gap-2 mb-4">
        <FileSpreadsheet className="w-5 h-5 text-emerald-600"/>
        <h2 className="font-heading text-lg font-bold">Google Sheets Sync <span className="text-xs font-mono-accent uppercase tracking-widest bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded ml-1">Meta Lead Ads</span></h2>
      </div>
      <p className="text-sm text-zinc-600 mb-4">
        Auto-import Meta Lead Ads from your Google Sheet into this business. Auto-detects Meta's native connector columns, dedupes on the Meta lead id, normalises phone to E.164, and skips test rows. All standard LeadForge new-lead automations fire on every synced lead.
      </p>

      {/* Iter 2026-02 — Tenant-routing confirmation banner.
          The API key is server-bound to this business, so every key you
          generate from this page routes leads exclusively here. This banner
          makes that visible so agencies with multiple LeadForge accounts
          don't have to guess which one is active. */}
      {businessName && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-2" data-testid="sheets-sync-target-banner">
          <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>
          <div className="text-sm text-emerald-900">
            <div>Every key you generate here routes leads to <b>{businessName}</b> only. Server-side enforced — nothing in the Apps Script can send leads to another account.</div>
            <div className="text-xs text-emerald-700 mt-0.5">Managing multiple businesses? Log into the other business first, then come back here to generate its own key.</div>
          </div>
        </div>
      )}

      {/* API keys */}
      <div className="border border-zinc-100 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-zinc-400"/>
            <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">Sheets Sync API keys</div>
          </div>
          <Button onClick={createKey} disabled={creating} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sheets-sync-create-key-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Plus className="w-4 h-4 mr-1"/> Generate key</>}
          </Button>
        </div>
        {loading ? <div className="text-center text-zinc-400 py-4"><Loader2 className="w-5 h-5 animate-spin inline"/></div>
        : keys.length === 0 ? <div className="text-center text-zinc-400 text-sm py-4">No sync keys yet. Generate one to get started.</div>
        : (
          <div className="divide-y divide-zinc-100">
            {keys.map((k) => (
              <div key={k.id} className="py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`sheets-sync-key-${k.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{k.name}</div>
                  <div className="text-xs font-mono-accent text-zinc-500">{k.prefix}·••••••  · used {k.use_count || 0}× {k.last_used_at ? "· last " + new Date(k.last_used_at).toLocaleDateString() : ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  {k.revoked_at ? (
                    <span className="text-xs text-rose-500 inline-flex items-center gap-1"><ShieldOff className="w-3 h-3"/> Revoked</span>
                  ) : (
                    <>
                      <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3"/> Active</span>
                      <Button size="sm" variant="outline" onClick={() => showScript(k.id)} data-testid={`sheets-sync-script-btn-${k.id}`}>
                        <ClipboardCheck className="w-4 h-4 mr-1"/> Install script
                      </Button>
                      <button onClick={() => revokeKey(k.id)} className="text-rose-500 hover:bg-rose-50 rounded p-1" title="Revoke"><Trash2 className="w-4 h-4"/></button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Column mappings */}
      <div className="border border-zinc-100 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-zinc-400"/>
            <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">Column mapping overrides</div>
            <span className="text-[11px] text-zinc-500">— auto-detect handles Meta's connector out of the box. Only add an override if a column isn't matching correctly.</span>
          </div>
          <Button size="sm" variant="outline" onClick={() => setMappingEditor({ spreadsheet_id: "", tab_name: "", nickname: "", headers_raw: "", mapping: {}, ignore_columns: [] })} data-testid="sheets-sync-add-mapping-btn">
            <Plus className="w-4 h-4 mr-1"/> Add mapping
          </Button>
        </div>
        {mappings.length === 0 ? (
          <div className="text-center text-zinc-400 text-sm py-3">No overrides. Auto-detect is in effect.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {mappings.map((m) => (
              <div key={m.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{m.nickname || m.spreadsheet_id}</div>
                  <div className="text-xs text-zinc-500 font-mono-accent">
                    {m.tab_name ? `tab: ${m.tab_name}` : "any tab"} · {Object.keys(m.mapping || {}).length} column{Object.keys(m.mapping || {}).length === 1 ? "" : "s"} mapped
                    {m.ignore_columns?.length ? ` · ${m.ignore_columns.length} ignored` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setMappingEditor({
                    id: m.id, spreadsheet_id: m.spreadsheet_id, tab_name: m.tab_name, nickname: m.nickname,
                    headers_raw: Object.keys(m.mapping || {}).join(", "),
                    mapping: m.mapping || {},
                    ignore_columns: m.ignore_columns || [],
                  })}>Edit</Button>
                  <button onClick={() => deleteMapping(m.id)} className="text-rose-500 hover:bg-rose-50 rounded p-1" title="Delete"><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Newly-created raw key dialog */}
      <Dialog open={!!newlyCreatedKey} onOpenChange={(v) => { if (!v) setNewlyCreatedKey(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600"/> Copy your API key now</DialogTitle>
            <DialogDescription>
              This is the only time the raw key will be shown. LeadForge only stores the hash — you can't retrieve it later. Paste it into the Apps Script placeholder in the next step.
            </DialogDescription>
          </DialogHeader>
          {businessName && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              This key routes leads exclusively to <b>{businessName}</b>.
            </div>
          )}
          <div className="bg-zinc-900 text-emerald-300 font-mono-accent text-xs p-3 rounded break-all select-all" data-testid="sheets-sync-raw-key">{newlyCreatedKey?.key}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewlyCreatedKey(null)}>I've saved it</Button>
            <Button onClick={() => copyToClipboard(newlyCreatedKey?.key || "", "API key copied")} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Copy className="w-4 h-4 mr-1"/> Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Script + install steps dialog */}
      <Dialog open={!!scriptModal} onOpenChange={(v) => { if (!v) setScriptModal(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-600"/> Install the Apps Script</DialogTitle>
            <DialogDescription>
              Copy the script into your spreadsheet's Apps Script editor. It takes about 90 seconds.
            </DialogDescription>
          </DialogHeader>
          {scriptModal?.business_name && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="sheets-sync-script-target">
              <ShieldCheck className="w-4 h-4 inline text-emerald-600 mr-1 -mt-0.5"/>
              Leads from this sheet will land in <b>{scriptModal.business_name}</b>. Verify anytime via LeadForge → Test API key inside the sheet.
            </div>
          )}
          <div className="overflow-y-auto pr-2 flex-1">
            <ol className="text-sm space-y-1.5 mb-4 text-zinc-700">
              {(scriptModal?.install_steps || []).map((s, i) => <li key={i}>{s}</li>)}
              <li className="text-zinc-500 italic">Tip: keep this window open — you'll need to swap <code className="text-rose-600">__PASTE_YOUR_API_KEY_HERE__</code> for the key you copied a moment ago.</li>
            </ol>
            <div className="relative">
              <Button size="sm" onClick={() => copyToClipboard(scriptModal?.script || "", "Script copied")}
                      className="absolute top-2 right-2 z-10 bg-zinc-800 hover:bg-zinc-700 text-white" data-testid="sheets-sync-copy-script-btn">
                <Copy className="w-3 h-3 mr-1"/> Copy
              </Button>
              <pre className="bg-zinc-900 text-emerald-100 text-[11px] p-4 rounded max-h-[45vh] overflow-auto font-mono-accent whitespace-pre-wrap">
                {scriptModal?.script}
              </pre>
            </div>
          </div>
          <DialogFooter>
            <a href="https://script.google.com/home" target="_blank" rel="noreferrer">
              <Button variant="outline"><ExternalLink className="w-4 h-4 mr-1"/> Open Apps Script</Button>
            </a>
            <Button onClick={() => setScriptModal(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mapping editor */}
      {mappingEditor && (
        <MappingEditor
          editor={mappingEditor}
          canonicalFields={canonicalFields}
          onCancel={() => setMappingEditor(null)}
          onSaved={() => { setMappingEditor(null); load(); }}
        />
      )}
    </section>
  );
}

function MappingEditor({ editor, canonicalFields, onCancel, onSaved }) {
  const [state, setState] = useState(editor);
  const [autoPreview, setAutoPreview] = useState({});
  const [previewing, setPreviewing] = useState(false);
  const headersList = useMemo(
    () => (state.headers_raw || "").split(",").map((h) => h.trim()).filter(Boolean),
    [state.headers_raw]
  );

  const preview = async () => {
    setPreviewing(true);
    try {
      // Auto-detect preview uses same regex logic as the intake endpoint.
      // We call the API-authed variant would require a key; do it client-side.
      const map = {};
      const rules = [
        { c: "meta_lead_id",    p: [/^id$/, /lead[_ ]?id/, /meta[_ ]?id/] },
        { c: "created_time",    p: [/created[_ ]?time/, /^timestamp$/, /^date$/, /^submitted[_ ]?at$/] },
        { c: "first_name",      p: [/^first[_ ]?name$/] },
        { c: "last_name",       p: [/^last[_ ]?name$/] },
        { c: "name",            p: [/^full[_ ]?name$/, /^name$/, /customer[_ ]?name/, /contact[_ ]?name/] },
        { c: "phone",           p: [/phone[_ ]?number/, /^phone$/, /^mobile$/, /^tel$/, /^cell$/] },
        { c: "email",           p: [/^email$/, /^e[_-]?mail$/, /email[_ ]?address/] },
        { c: "street_address",  p: [/street[_ ]?address/, /^address$/, /^address[_ ]?1$/, /^street$/] },
        { c: "city",            p: [/^city$/, /^town$/] },
        { c: "state",           p: [/^state$/, /^province$/, /^region$/] },
        { c: "zip",             p: [/^zip$/, /zip[_ ]?code/, /^postal[_ ]?code$/, /^post[_ ]?code$/] },
        { c: "campaign_name",   p: [/campaign[_ ]?name/] },
        { c: "ad_name",         p: [/^ad[_ ]?name$/] },
        { c: "adset_name",      p: [/adset[_ ]?name/, /ad[_ ]?set[_ ]?name/] },
        { c: "form_name",       p: [/form[_ ]?name/] },
        { c: "platform",        p: [/^platform$/] },
        { c: "is_organic",      p: [/is[_ ]?organic/] },
        { c: "service_requested", p: [/service/, /what.*do.*you.*need/, /interested[_ ]?in/, /what.*are.*you.*looking/] },
      ];
      for (const h of headersList) {
        const lower = h.toLowerCase();
        for (const r of rules) {
          if (r.p.some((rx) => rx.test(lower))) { map[h] = r.c; break; }
        }
      }
      setAutoPreview(map);
      setState((s) => ({ ...s, mapping: { ...map, ...s.mapping } }));
    } finally { setPreviewing(false); }
  };

  const setMap = (h, v) => setState((s) => {
    const nm = { ...(s.mapping || {}) };
    if (v === "__auto__") { delete nm[h]; }
    else nm[h] = v;
    return { ...s, mapping: nm };
  });

  const save = async () => {
    if (!state.spreadsheet_id?.trim()) { toast.error("Spreadsheet ID required (get it from the URL between /d/ and /edit)"); return; }
    try {
      await api.post("/integrations/sheets-sync/mappings", {
        spreadsheet_id: state.spreadsheet_id.trim(),
        tab_name: state.tab_name || "",
        nickname: state.nickname || "",
        mapping: Object.fromEntries(Object.entries(state.mapping || {}).filter(([, v]) => v && v !== "__auto__")),
        ignore_columns: state.ignore_columns || [],
      });
      toast.success("Mapping saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="sheets-sync-mapping-editor">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Column mapping</DialogTitle>
          <DialogDescription>Paste the header row from your sheet and override any columns that aren't mapping correctly.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 overflow-y-auto flex-1 pr-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Nickname</Label>
              <Input value={state.nickname || ""} onChange={(e) => setState((s) => ({ ...s, nickname: e.target.value }))} placeholder="Meta Lead Ads · Rock River"/>
            </div>
            <div>
              <Label>Tab name <span className="text-zinc-400 font-normal text-xs">(blank = first tab)</span></Label>
              <Input value={state.tab_name || ""} onChange={(e) => setState((s) => ({ ...s, tab_name: e.target.value }))} placeholder="Leads"/>
            </div>
          </div>
          <div>
            <Label>Spreadsheet ID <span className="text-zinc-400 font-normal text-xs">— from your sheet URL between /d/ and /edit</span></Label>
            <Input value={state.spreadsheet_id || ""} onChange={(e) => setState((s) => ({ ...s, spreadsheet_id: e.target.value }))} className="font-mono-accent" placeholder="1AbC..."/>
          </div>
          <div>
            <Label>Header row <span className="text-zinc-400 font-normal text-xs">— paste comma-separated headers from row 1</span></Label>
            <Input value={state.headers_raw || ""} onChange={(e) => setState((s) => ({ ...s, headers_raw: e.target.value }))} placeholder="id, created_time, full_name, phone_number, email, campaign_name, ..."/>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={preview} disabled={previewing || !headersList.length}>
              {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Sparkles className="w-4 h-4 mr-1"/>} Auto-detect
            </Button>
            <span className="text-xs text-zinc-500">Preview then tweak per column below.</span>
          </div>
          {headersList.length > 0 && (
            <div className="border border-zinc-100 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                  <tr><th className="text-left px-3 py-2">Sheet column</th><th className="text-left px-3 py-2">Maps to</th></tr>
                </thead>
                <tbody>
                  {headersList.map((h) => {
                    const cur = state.mapping?.[h] || autoPreview[h] || "__auto__";
                    return (
                      <tr key={h} className="border-t border-zinc-100">
                        <td className="px-3 py-2 font-mono-accent">{h}</td>
                        <td className="px-3 py-2">
                          <Select value={cur} onValueChange={(v) => setMap(h, v)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue/></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__auto__">(auto-detect / append to notes)</SelectItem>
                              {canonicalFields.filter((f) => f !== "ignore").map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                              <SelectItem value="ignore">— ignore this column —</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sheets-sync-mapping-save-btn">Save mapping</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
